import { useCurrentWorkspace } from "@accurx/auth";
import { Ds, Feedback } from "@accurx/design";
import { NetworkError } from "@accurx/shared";
import { useDirectory } from "domains/directory/context/DirectoryProvider";
import { useDeleteContact } from "domains/directory/hooks/mutations/useDeleteContactMutation";
import { toast } from "react-toastify";

type DirectoryRemoveContactProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    contactId: string;
};

export const DirectoryRemoveContact = ({
    open,
    onOpenChange,
    contactId,
}: DirectoryRemoveContactProps) => {
    const { orgId: workspaceId } = useCurrentWorkspace();
    const { setSelectedContactId, setView, setContactOrigin } = useDirectory();

    const { mutate: deleteContact } = useDeleteContact({
        onSuccess: () => {
            setSelectedContactId(undefined);
            setView("Search");
            setContactOrigin(undefined);
            toast(
                <Feedback
                    colour="success"
                    title="Contact removed successfully"
                />,
            );
        },
        onError: (error) => {
            if (error instanceof NetworkError) {
                toast(
                    <Feedback
                        colour="error"
                        title="Couldn’t remove contact. Please check your connection and try again."
                    />,
                );
                return;
            }
            toast(
                <Feedback
                    colour="error"
                    title="Couldn’t remove contact. Please try again."
                />,
            );
        },
    });

    const onDelete = () => {
        deleteContact({ contactId, workspaceId });
        onOpenChange(false);
    };

    return (
        <Ds.Modal open={open} onOpenChange={onOpenChange}>
            <Ds.Modal.Section>
                <Ds.Flex flexDirection="column" gap="2">
                    <Ds.Modal.Title>Remove contact?</Ds.Modal.Title>
                    <Ds.Text>
                        This contact will no longer be available to anyone in
                        your workspace
                    </Ds.Text>
                    <Ds.Flex gap="2" justifyContent="flex-end">
                        <Ds.Button
                            appearance="secondary"
                            onClick={() => onOpenChange(false)}
                        >
                            Cancel
                        </Ds.Button>
                        <Ds.Button onClick={onDelete}>Remove</Ds.Button>
                    </Ds.Flex>
                </Ds.Flex>
            </Ds.Modal.Section>
        </Ds.Modal>
    );
};
